import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import BreakdownTopValuesControl from "./BreakdownTopValuesControl";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";

const tableStaticRowClass = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

function metricRowKey(label) {
  const s = String(label ?? "").trim() || "(none)";
  return `rm-${encodeURIComponent(s).replace(/%/g, "_")}`;
}

function inferSortValueFromDisplay(display, metricIsRetentionRate) {
  const s = String(display ?? "").replace(/,/g, "").trim();
  if (metricIsRetentionRate) {
    const m = s.match(/^([\d.]+)\s*%/);
    return m ? Number(m[1]) : 0;
  }
  const n = Number(s.replace(/[^0-9.\-eE+]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function compareMetricRows(a, b, sortMode) {
  if (sortMode === "segment-asc") {
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  }
  if (sortMode === "segment-desc") {
    return String(b.label || "").localeCompare(String(a.label || ""), undefined, { sensitivity: "base" });
  }
  const ascending = sortMode === "value-asc";
  const va = typeof a.sortValue === "number" && Number.isFinite(a.sortValue) ? a.sortValue : null;
  const vb = typeof b.sortValue === "number" && Number.isFinite(b.sortValue) ? b.sortValue : null;
  const aMissing = va == null;
  const bMissing = vb == null;
  if (aMissing && bMissing) {
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  }
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (va === vb) {
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  }
  return ascending ? va - vb : vb - va;
}

export default function RetentionMetricTable({
  label = "Retention",
  value = "0%",
  rows = null,
  /** Used to parse Value cells when sortValue is not provided on each row */
  metricIsRetentionRate = true,
  /** Query breakdown property label, e.g. "Country" */
  breakdownDimensionLabel = "",
  breakdownChartTopN,
  onBreakdownChartTopNChange,
}) {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const breakdownHeaderTitle = String(breakdownDimensionLabel || "").trim() || "Breakdown name";
  const [sortMode, setSortMode] = useState("segment-asc");
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const rowsNormalized = useMemo(() => {
    if (!hasRows) return [];
    return rows.map((r, i) => {
      const sortValue =
        typeof r.sortValue === "number" && Number.isFinite(r.sortValue)
          ? r.sortValue
          : inferSortValueFromDisplay(r?.value, metricIsRetentionRate);
      return {
        ...r,
        sortValue,
        rowKey: metricRowKey(r?.label ?? i),
      };
    });
  }, [rows, hasRows, metricIsRetentionRate]);

  const sortedRows = useMemo(() => {
    if (!hasRows) return [];
    const copy = [...rowsNormalized];
    copy.sort((a, b) => compareMetricRows(a, b, sortMode));
    return copy;
  }, [rowsNormalized, hasRows, sortMode]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const updatePos = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, left: r.left });
    };
    updatePos();
    const onDoc = (e) => {
      const t = e.target;
      if (menuRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const showTopControl =
    hasRows && typeof onBreakdownChartTopNChange === "function" && breakdownChartTopN != null;

  return (
    <div className="w-full overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className={tableHeaderRowClass}>
            <th
              className={tableHeaderCellClass}
              style={{ borderRight: `1px solid ${theme.border}` }}
            >
              {hasRows ? (
                <div className="flex flex-col items-start gap-1" style={{ textTransform: "none" }}>
                  <div className="inline-flex items-center gap-1" ref={triggerRef}>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">
                      {breakdownHeaderTitle}
                      {rows.length > 0 ? ` ${rows.length}` : ""}
                    </span>
                    <button
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      onClick={() => setMenuOpen((o) => !o)}
                      className="inline-flex items-center justify-center rounded p-0.5"
                      style={{
                        color: theme.textMuted,
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                      title="Sort breakdown"
                    >
                      <svg width="10" height="10" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                    {menuOpen &&
                      createPortal(
                        <div
                          ref={menuRef}
                          role="menu"
                          style={{
                            position: "fixed",
                            top: menuPos.top,
                            left: menuPos.left,
                            minWidth: 200,
                            padding: "8px 0",
                            backgroundColor: theme.bgRaised || "var(--surface-base)",
                            border: `1px solid ${theme.border}`,
                            borderRadius: 10,
                            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
                            zIndex: 9999,
                            fontFamily: "Inter, sans-serif",
                            textTransform: "none",
                          }}
                        >
                          {[
                            {
                              key: "seg-asc",
                              label: "Segment A → Z",
                              active: sortMode === "segment-asc",
                              onSelect: () => {
                                setSortMode("segment-asc");
                                setMenuOpen(false);
                              },
                            },
                            {
                              key: "seg-desc",
                              label: "Segment Z → A",
                              active: sortMode === "segment-desc",
                              onSelect: () => {
                                setSortMode("segment-desc");
                                setMenuOpen(false);
                              },
                            },
                            {
                              key: "val-asc",
                              label: "Value 1 → 100",
                              active: sortMode === "value-asc",
                              onSelect: () => {
                                setSortMode("value-asc");
                                setMenuOpen(false);
                              },
                            },
                            {
                              key: "val-desc",
                              label: "Value 100 → 1",
                              active: sortMode === "value-desc",
                              onSelect: () => {
                                setSortMode("value-desc");
                                setMenuOpen(false);
                              },
                            },
                          ].map((opt) => (
                            <div
                              key={opt.key}
                              role="menuitem"
                              onClick={opt.onSelect}
                              className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                              style={{
                                backgroundColor: opt.active ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                                color: opt.active ? (theme.textPrimary || "var(--text-primary)") : (theme.textSecondary || "var(--text-secondary)"),
                                fontWeight: opt.active ? 600 : 500,
                                borderLeft: opt.active ? `2px solid ${theme.primary}` : "2px solid transparent",
                              }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                  </div>
                  {showTopControl && (
                    <BreakdownTopValuesControl value={breakdownChartTopN} onChange={onBreakdownChartTopNChange} />
                  )}
                </div>
              ) : (
                "Retention"
              )}
            </th>
            <th className={tableHeaderCellClass}>
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {hasRows ? (
            sortedRows.map((r) => (
              <tr key={r.rowKey} className={tableStaticRowClass}>
                <td
                  className={`${tableDataCellClass} whitespace-nowrap`}
                  style={{
                    color: theme.text,
                    borderRight: `1px solid ${theme.border}`,
                    fontWeight: 500,
                    minWidth: 200,
                  }}
                  title={r?.label}
                >
                  {r?.label}
                </td>
                <td className={`${tableDataCellClass} text-right tabular-nums whitespace-nowrap`} style={{ color: theme.text, fontWeight: 600 }}>
                  {r?.value}
                </td>
              </tr>
            ))
          ) : (
            <tr className={tableStaticRowClass}>
              <td
                className={`${tableDataCellClass} whitespace-nowrap`}
                style={{
                  color: theme.text,
                  borderRight: `1px solid ${theme.border}`,
                  fontWeight: 500,
                  minWidth: 200,
                }}
                title={label}
              >
                {label}
              </td>
              <td className={`${tableDataCellClass} text-right tabular-nums whitespace-nowrap`} style={{ color: theme.text, fontWeight: 600 }}>
                {value}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </div>
  );
}
